import { INTERACTION } from 'app/shopify/generated/interaction';
import { useState } from 'react';
import { Pressable, StyleSheet, type PressableProps, type StyleProp, type ViewStyle } from 'react-native';
import { Box, useTheme } from 'app/shopify/theme';

export type NavigationState = 'enabled' | 'hover' | 'pressed' | 'focus' | 'disabled';

interface NavigationPressableProps extends Omit<PressableProps, 'children' | 'style'> {
  children: React.ReactNode;
  state?: NavigationState;
  style?: StyleProp<ViewStyle>;
  /** Publish buttons use a brand overlay; tabs use the neutral state layer. */
  brand?: boolean;
}

/** Shared interaction chrome for the navigation-specific Figma controls. */
export function NavigationPressable({
  children,
  state = 'enabled',
  disabled = false,
  style,
  brand = false,
  onFocus,
  onBlur,
  onHoverIn,
  onHoverOut,
  accessibilityState,
  ...props
}: NavigationPressableProps) {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const isDisabled = disabled || state === 'disabled';
  return (
    <Pressable
      {...props}
      accessibilityRole={props.accessibilityRole ?? 'button'}
      accessibilityState={{ ...accessibilityState, disabled: isDisabled }}
      disabled={isDisabled}
      onFocus={(event) => {
        setFocused(true);
        onFocus?.(event);
      }}
      onBlur={(event) => {
        setFocused(false);
        onBlur?.(event);
      }}
      onHoverIn={(event) => {
        setHovered(true);
        onHoverIn?.(event);
      }}
      onHoverOut={(event) => {
        setHovered(false);
        onHoverOut?.(event);
      }}
      style={[
        { position: 'relative', opacity: isDisabled ? INTERACTION.opacity['040'] : INTERACTION.opacity['100'] },
        style,
      ]}
    >
      {({ pressed }) => {
        const showPress = !isDisabled && (pressed || state === 'pressed');
        const showHover = !isDisabled && (hovered || state === 'hover');
        const overlay = showPress
          ? theme.colors[brand ? 'state-layer.press-brand' : 'state-layer.press']
          : showHover
            ? theme.colors[brand ? 'state-layer.hover-brand' : 'state-layer.hover']
            : undefined;
        return (
          <>
            {overlay && (
              <Box
                pointerEvents="none"
                style={[
                  StyleSheet.absoluteFill,
                  { borderRadius: StyleSheet.flatten(style)?.borderRadius, backgroundColor: overlay },
                ]}
              />
            )}
            {children}
            {!isDisabled && (focused || state === 'focus') && (
              <Box
                pointerEvents="none"
                style={[
                  StyleSheet.absoluteFill,
                  {
                    borderRadius: StyleSheet.flatten(style)?.borderRadius,
                    borderColor: theme.colors['border.focused'],
                    borderWidth: theme.borderWidths['border-width.emphasized'],
                  },
                ]}
              />
            )}
          </>
        );
      }}
    </Pressable>
  );
}
